"use client";

// ============================================================
// CLUBHUB – Dokumente: der Ordnerbaum
// ============================================================
// Links neben dem Inhalt, wie die Sammlungen im DAM: oben die
// Startseite, darunter die drei Laufwerke mit ihren Ordnern, unten
// die Vorlagen. Der Baum ist der groesste Filter der Ablage — ein
// Klick oeffnet den Ordner, der Winkel klappt nur auf und zu.
//
// Jeder Ordner ist ein Ablageziel: Dateien aus dem Inhalt darauf
// gezogen werden verschoben, Dateien vom Rechner hochgeladen. Wo
// ein Ordner enger eingestellt ist als sein Laufwerk, steht das
// Zeichen seiner Stufe neben dem Namen — Einschraenkungen sieht man,
// ohne jeden Ordner zu oeffnen.
//
// Die Zahl rechts zaehlt die Dateien samt Unterordnern.
// ============================================================

import { useState } from "react";
import Link from "next/link";
import { ChevronRight, FileStack, FolderPlus, LayoutGrid } from "lucide-react";
import { MehrKnopf } from "@/components/ui/zeichen-knopf";
import { cn } from "@/lib/utils";
import { formatDecimal } from "@/lib/number-format";
import { kinderVon, pfadZu, wirksameSicht } from "@/lib/dokumente/format";
import type { FolderView } from "@/lib/dokumente/views";
import { useAblage } from "./ablage-kontext";
import { SICHT_ZEICHEN } from "./dialoge";
import { OrdnerMenue, useAblageziel } from "./inhalt";

const ZEILE = "text-body-sm flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left duration-fast transition-colors";

function Zweig({
  ordner,
  tiefe,
  aktiv,
  istOffen,
  umschalten,
}: {
  ordner: FolderView;
  tiefe: number;
  aktiv: string;
  istOffen: (id: string) => boolean;
  umschalten: (id: string) => void;
}) {
  const { bestand, zahlen, zumOrdner } = useAblage();
  const { ueber, ziel } = useAblageziel(ordner.id);
  const kinder = kinderVon(ordner.id, bestand.folders);
  const offen = istOffen(ordner.id);
  const istAktiv = aktiv === ordner.id;
  // Das Zeichen zeigt, wer WIRKLICH sieht (die engste Stufe auf dem Weg),
  // und steht nur dort, wo sich das gegenueber dem Elternordner aendert.
  const sicht = wirksameSicht(ordner.id, bestand.folders);
  const elternSicht = wirksameSicht(ordner.parentId, bestand.folders);
  const eigeneStufe =
    ordner.parentId === null ||
    sicht.visibility !== elternSicht.visibility ||
    (sicht.visibility === "GROUPS" && sicht.groupIds.join() !== elternSicht.groupIds.join());
  const Zeichen = SICHT_ZEICHEN[sicht.visibility];
  const anzahl = zahlen.get(ordner.id)?.dateien ?? 0;

  return (
    <li>
      <div
        {...ziel}
        className={cn(
          "group flex items-center rounded-lg pr-1",
          istAktiv ? "bg-bg-tertiary" : "hover:bg-bg-secondary",
          ueber && "ring-accent ring-2 ring-inset",
        )}
        style={{ paddingLeft: `${4 + tiefe * 14}px` }}
      >
        <button
          type="button"
          onClick={() => kinder.length > 0 && umschalten(ordner.id)}
          aria-label={offen ? `${ordner.name} zuklappen` : `${ordner.name} aufklappen`}
          aria-expanded={kinder.length > 0 ? offen : undefined}
          tabIndex={kinder.length > 0 ? 0 : -1}
          className={cn("grid h-6 w-5 shrink-0 place-items-center rounded", kinder.length === 0 && "pointer-events-none")}
        >
          <ChevronRight
            className={cn(
              "text-text-tertiary duration-fast h-3.5 w-3.5 transition-transform",
              offen && "rotate-90",
              kinder.length === 0 && "opacity-0",
            )}
            aria-hidden
          />
        </button>
        <button
          type="button"
          onClick={() => {
            zumOrdner(ordner.id);
            if (!offen && kinder.length > 0) umschalten(ordner.id);
          }}
          aria-current={istAktiv ? "page" : undefined}
          title={ordner.name}
          className={cn(ZEILE, istAktiv ? "text-text-primary font-medium" : "text-text-secondary")}
        >
          {ordner.parentId === null && <Zeichen className="text-text-tertiary h-3.5 w-3.5 shrink-0" aria-hidden />}
          <span className="truncate">{ordner.name}</span>
          {ordner.parentId !== null && eigeneStufe && (
            <Zeichen className="text-text-tertiary h-3 w-3 shrink-0" aria-label="eigene Sichtbarkeit" />
          )}
          <span className="numeric text-caption text-text-tertiary ml-auto shrink-0 pl-1">
            {anzahl > 0 ? formatDecimal(anzahl) : ""}
          </span>
        </button>
        <OrdnerMenue ordner={ordner} trigger={<MehrKnopf groesse="winzig" nurBeimHinzeigen label={`${ordner.name}: weitere Aktionen`} />} />
      </div>
      {offen && kinder.length > 0 && (
        <ul className="mt-0.5 space-y-0.5">
          {kinder.map((k) => (
            <Zweig key={k.id} ordner={k} tiefe={tiefe + 1} aktiv={aktiv} istOffen={istOffen} umschalten={umschalten} />
          ))}
        </ul>
      )}
    </li>
  );
}

export function Ordnerbaum({ bereich }: { bereich: "ablage" | "vorlagen" }) {
  const { bestand, daten, ansicht, zumOrdner, oeffnen } = useAblage();
  const aktiv = bereich === "ablage" ? ansicht.ordner : "";
  const [auf, setAuf] = useState<Set<string>>(new Set());
  const [zu, setZu] = useState<Set<string>>(new Set());

  // Der Weg zum offenen Ordner ist immer aufgeklappt — auch wenn man
  // ueber den Inhalt hineingegangen ist und ein Ast davor zu war.
  const [letzterAktiv, setLetzterAktiv] = useState(aktiv);
  const wegZumAktiven = new Set(pfadZu(aktiv || null, bestand.folders).map((f) => f.id));
  if (letzterAktiv !== aktiv) {
    setLetzterAktiv(aktiv);
    if ([...wegZumAktiven].some((id) => zu.has(id))) setZu(new Set([...zu].filter((id) => !wegZumAktiven.has(id))));
  }

  const istOffen = (id: string) => {
    if (zu.has(id)) return false;
    if (auf.has(id)) return true;
    const f = bestand.folders.find((x) => x.id === id);
    return f?.parentId === null || wegZumAktiven.has(id);
  };

  function umschalten(id: string) {
    if (istOffen(id)) {
      setZu(new Set([...zu, id]));
      setAuf(new Set([...auf].filter((x) => x !== id)));
    } else {
      setAuf(new Set([...auf, id]));
      setZu(new Set([...zu].filter((x) => x !== id)));
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <nav aria-label="Ordner" className="min-h-0 flex-1 overflow-y-auto p-2">
        <ul className="space-y-0.5">
          <li>
            <button
              type="button"
              onClick={() => zumOrdner(null)}
              aria-current={bereich === "ablage" && !aktiv ? "page" : undefined}
              className={cn(
                ZEILE,
                "w-full rounded-lg pr-2 pl-[26px]",
                bereich === "ablage" && !aktiv ? "bg-bg-tertiary text-text-primary font-medium" : "text-text-secondary hover:bg-bg-secondary",
              )}
            >
              <LayoutGrid className="text-text-tertiary h-3.5 w-3.5 shrink-0" aria-hidden />
              Übersicht
            </button>
          </li>
        </ul>
        <ul className="mt-2 space-y-0.5">
          {kinderVon(null, bestand.folders).map((f) => (
            <Zweig key={f.id} ordner={f} tiefe={0} aktiv={aktiv} istOffen={istOffen} umschalten={umschalten} />
          ))}
        </ul>
        <div className="border-border-subtle mt-3 border-t pt-2">
          <Link
            href="/dokumente/vorlagen"
            aria-current={bereich === "vorlagen" ? "page" : undefined}
            className={cn(
              ZEILE,
              "w-full rounded-lg pr-2 pl-[26px]",
              bereich === "vorlagen" ? "bg-bg-tertiary text-text-primary font-medium" : "text-text-secondary hover:bg-bg-secondary",
            )}
          >
            <FileStack className="text-text-tertiary h-3.5 w-3.5 shrink-0" aria-hidden />
            Vorlagen
            <span className="numeric text-caption text-text-tertiary ml-auto">{formatDecimal(bestand.templates.length)}</span>
          </Link>
        </div>
      </nav>
      {daten.darfSchreiben && (
      <div className="border-border-subtle border-t p-2">
        <button
          type="button"
          onClick={() => oeffnen({ art: "ordner-neu", parentId: bestand.folders.some((f) => f.id === aktiv) ? aktiv : null })}
          className="text-body-sm text-text-secondary hover:bg-bg-secondary hover:text-text-primary duration-fast flex w-full items-center gap-2 rounded-lg px-2 py-1.5 transition-colors"
        >
          <FolderPlus className="h-4 w-4 shrink-0" aria-hidden />
          Neuer Ordner
        </button>
      </div>
      )}
    </div>
  );
}
